import { Injectable } from '@angular/core'
import { IdentityProviderName } from '@ftr/contracts/type/account'
import { CookieStorageService, LocalStorageService } from '@ftr/foundation'
import { PortalDomainService } from '../portal-domain'

const IDENTITY_PROVIDER = 'identityProvider'

const ID_TOKEN_KEY = 'oauth:id_token'
const REFRESH_TOKEN_KEY = 'oauth:refresh_token'
const ID_TOKEN_EXPIRES_AT_KEY = 'oauth:id_token_expires_at'
const ACCESS_TOKEN_AMR_CLAIM_KEY = 'oauth:access_token_amr_claim'
const ACCESS_TOKEN_AMR_CLAIM_HASH_KEY = 'oauth:access_token_amr_claim_hash'

/**
 * The identity provider + email combination. This will be used to compare
 * the existing session with a new login done on a different tab.
 */
export const AUTH_IDENTIFIER = 'authIdentifier'

@Injectable({
  providedIn: 'root',
})
export class AuthTokenStorageService {
  constructor(
    private readonly localStorageService: LocalStorageService,
    private readonly cookieStorage: CookieStorageService,
    private readonly portalDomainService: PortalDomainService,
  ) {}

  getIdentityProvider(): IdentityProviderName | undefined {
    return this.get<IdentityProviderName>(IDENTITY_PROVIDER)
  }

  getRefreshToken(): string | undefined {
    return this.get<string>(REFRESH_TOKEN_KEY)
  }

  getIdToken(): string | undefined {
    return this.get<string>(ID_TOKEN_KEY)
  }

  getIdTokenExpiresAt(): number | undefined {
    return this.get<number>(ID_TOKEN_EXPIRES_AT_KEY)
  }

  getAccessTokenAmrClaim(): string[] | undefined {
    const amrClaim = this.get<string[] | string>(ACCESS_TOKEN_AMR_CLAIM_KEY)
    if (Array.isArray(amrClaim)) {
      return amrClaim
    }
    return amrClaim?.toString().split(',')
  }

  getAccessTokenAmrClaimHash(): string | undefined {
    return this.get<string>(ACCESS_TOKEN_AMR_CLAIM_HASH_KEY)
  }

  getAuthIdentifier(): string | undefined {
    return this.get<string>(AUTH_IDENTIFIER)
  }

  removeTokenData(): void {
    this.remove(ID_TOKEN_KEY)
    this.remove(REFRESH_TOKEN_KEY)
    this.remove(ID_TOKEN_EXPIRES_AT_KEY)
    this.remove(ACCESS_TOKEN_AMR_CLAIM_KEY)
    this.remove(ACCESS_TOKEN_AMR_CLAIM_HASH_KEY)
  }

  storeTokens(idToken: string, refreshToken: string, idTokenExpiresAt: number, amrClaim: string[] | undefined): void {
    this.set(ID_TOKEN_KEY, idToken)
    this.set(REFRESH_TOKEN_KEY, refreshToken)
    this.set(ID_TOKEN_EXPIRES_AT_KEY, idTokenExpiresAt)
    this.set(ACCESS_TOKEN_AMR_CLAIM_KEY, amrClaim)
  }

  setAccessTokenAmrClaimHash(hashedAmrClaim: string): void {
    this.set(ACCESS_TOKEN_AMR_CLAIM_HASH_KEY, hashedAmrClaim)
  }

  setIdentityProvider(identityProviderName: IdentityProviderName): void {
    this.set(IDENTITY_PROVIDER, identityProviderName)
  }

  setAuthIdentifier(authIdentifier: string): void {
    this.set(AUTH_IDENTIFIER, authIdentifier)
  }

  removeIdentityProvider(): void {
    this.remove(IDENTITY_PROVIDER)
  }

  removeAuthIdentifier(): void {
    this.remove(AUTH_IDENTIFIER)
  }

  removeCognitoUserData(cognitoAppClientId: string, cognitoUserName: string): void {
    this.getStorageProvider().removeGlobal(
      `CognitoIdentityServiceProvider.${cognitoAppClientId}.${cognitoUserName}.userData`,
    )
  }

  isCookieStorage(): boolean {
    return this.portalDomainService.isPublicPortal() || this.portalDomainService.isCourtsPortal()
  }

  private get<T>(storageKey: string): T | undefined {
    return this.getStorageProvider().getGlobal<T>(storageKey)
  }

  private set(storageKey: string, data: any): void {
    this.getStorageProvider().setGlobal(storageKey, data)
  }

  private remove(storageKey: string): void {
    this.getStorageProvider().removeGlobal(storageKey)
  }

  private getStorageProvider(): LocalStorageService | CookieStorageService {
    if (this.isCookieStorage()) {
      return this.cookieStorage
    }
    return this.localStorageService
  }
}
